import { Button, HStack, Image, Stack } from '@chakra-ui/react'
import { Token } from '@chakra-ui/styled-system/dist/types/utils'
import { CSS as DnDCSS } from "@dnd-kit/utilities";
import { useSortable } from '@dnd-kit/sortable'
import * as CSS from 'csstype'
import clsx from 'clsx'
import React, { FC, memo } from 'react'
import { GoArrowDown, GoArrowUp, GoX } from 'react-icons/go'
import { Attachment } from '../../generated'
import { isContentTypeImage } from '../util/isContentTypeImage'
import { DownloadLink } from './DownloadLink'
import styles from "./UploadedFileRow.module.css";
import { DragHandleIcon } from '@chakra-ui/icons';

const _UploadedFileRowInner: FC<{
  attachment: Attachment
  canEdit?: boolean
  canMove?: boolean
  handleDeleteFile: () => void
  preview?: boolean
  isUpDisabled: boolean
  isDownDisabled: boolean
  upDownFuncs?: {
    index: number
    up: (index: number) => void
    down: (index: number) => void
  }
  fontSize?: Token<CSS.Property.FontSize | number, 'fontSizes'>,
}> = ({
  attachment,
  canEdit,
  canMove,
  handleDeleteFile,
  preview,
  isUpDisabled,
  isDownDisabled,
  upDownFuncs,
  fontSize,
}) => {
    return (
      <div key={attachment.id}>
        {preview && isContentTypeImage(attachment.contentType) && (
          <Image
            src={attachment.url}
            alt={attachment.name}
            boxSize="160px"
            cursor="pointer"
            objectFit="cover"
          />
        )}
        <Stack direction={`row`} alignItems={'center'} spacing={'4px'}>
          <DownloadLink
            url={attachment.url}
            name={attachment.name}
            withIcon={false}
            fontSize={fontSize}
          />
          {canEdit && (
            <HStack spacing={'2px'}>
              {canMove && upDownFuncs && (
                <>
                  <Button
                    type="button"
                    size={'xs'}
                    onClick={() => upDownFuncs.up(upDownFuncs.index)}
                    isDisabled={isUpDisabled ?? false}
                  >
                    <GoArrowUp />
                  </Button>
                  <Button
                    type="button"
                    size={'xs'}
                    onClick={() => upDownFuncs.down(upDownFuncs.index)}
                    isDisabled={isDownDisabled ?? false}
                  >
                    <GoArrowDown />
                  </Button>
                </>
              )}
              <Button
                type="button"
                size={'xs'}
                onClick={handleDeleteFile}
                colorScheme={'red'}
                w={'14px'}
                h={'14px'}
                minW={'14px'}
                mt={1}
                px={0}
              >
                <GoX size={'10px'} />
              </Button>
            </HStack>
          )}
        </Stack>
      </div>
    )
  }

export interface UploadedFile {
  id: number
  name: string
  url: string
}

const _UploadedFileRow: FC<{
  attachment: Attachment
  canEdit?: boolean
  canMove?: boolean
  handleDeleteFile: () => void
  preview?: boolean
  isUpDisabled: boolean
  isDownDisabled: boolean
  upDownFuncs?: {
    index: number
    up: (index: number) => void
    down: (index: number) => void
  }
  fontSize?: Token<CSS.Property.FontSize | number, 'fontSizes'>
  canGrab?: boolean
}> = ({
  attachment,
  canEdit,
  canMove,
  handleDeleteFile,
  preview,
  isUpDisabled,
  isDownDisabled,
  upDownFuncs,
  fontSize,
  canGrab
}) => {
    const {
      isDragging,
      // 並び替えのつまみ部分に設定するプロパティ
      setActivatorNodeRef,
      attributes,
      listeners,
      // DOM全体に対して設定するプロパティ
      setNodeRef,
      transform,
      transition
    } = useSortable({ id: attachment.id });

    if (!canGrab) {
      return (<_UploadedFileRowInner
        attachment={attachment}
        canEdit={canEdit}
        canMove={canMove}
        handleDeleteFile={handleDeleteFile}
        preview={preview}
        isUpDisabled={isUpDisabled}
        isDownDisabled={isDownDisabled}
        upDownFuncs={upDownFuncs}
        fontSize={fontSize}
      />)
    }

    return (
      <div
        key={attachment.id}
        ref={setNodeRef}
        className={clsx(styles.ItemWrapper, {
          // ドラッグ中の項目は一番上に表示して欲しいのでz-indexを設定するCSSを当てる
          [styles._active]: isDragging
        })}
        style={{
          transform: DnDCSS.Transform.toString(transform),
          transition
        }}
      >
        <div className={styles.Item}>
          {/* つまみ部分 */}
          <HStack>
            <div
              ref={setActivatorNodeRef}
              className="mdi mdi-drag"
              style={{
                cursor: isDragging ? "grabbing" : "grab"
              }}
              {...attributes}
              {...listeners}
            >
              <DragHandleIcon />
            </div>
            <_UploadedFileRowInner
              attachment={attachment}
              canEdit={canEdit}
              canMove={canMove}
              handleDeleteFile={handleDeleteFile}
              preview={preview}
              isUpDisabled={isUpDisabled}
              isDownDisabled={isDownDisabled}
              upDownFuncs={upDownFuncs}
              fontSize={fontSize}
            />
          </HStack>
        </div>
      </div>
    )
  }

export const UploadedFileRow = memo(_UploadedFileRow)
